// BudgetCommercialeMandatarioCard.tsx - VERSIONE CORRETTA
import React, { useState } from "react";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "../../../../../../redux/store";

import {
    BudgetCommerciale,
    fetchBudgetsCommercialiMandatari,
    BudgetsMandatariDettagliList
} from "../../budgetsSlice";

import { ErrorStatusTypes } from "components/common/errorsDeclarations";

import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Typography from "@mui/material/Typography";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Paper from "@mui/material/Paper";
import CircularProgress from "@mui/material/CircularProgress";
import Chip from "@mui/material/Chip";
import { styled } from "@mui/material/styles";

// Icons
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import {theme} from "../../../../../theme";
import { Link } from "react-router-dom";
import {Routes_path} from "../../../../../routerApp";

// Component per item con sfondo grigio
const ItemSmall = styled(Paper)(({ theme }) => ({
    ...theme.typography.body2,
    padding: theme.spacing(1),
    textAlign: 'center',
    color: (theme.vars ?? theme).palette.text.secondary,
    backgroundColor: theme.palette.grey[100],
    alignContent: 'center',
    alignItems: 'center',
    elevation: 2
}));

interface BudgetCommercialeMandatarioCardProps {
    budgetMandatario: BudgetCommerciale;
    stagione_id: number;
}

const BudgetCommercialeMandatarioCard = ({
                                             budgetMandatario,
                                             stagione_id
                                         }: BudgetCommercialeMandatarioCardProps) => {
    const dispatch = useAppDispatch();

    // Stati locali
    const [expanded, setExpanded] = useState(false);
    const [dataLoaded, setDataLoaded] = useState(false);
    const [loading, setLoading] = useState(false);

    // Selector per i dettagli degli item
    const budgetsMandatariDettagli = useSelector<RootState, BudgetsMandatariDettagliList>(
        (state) => state.budgetsState.budgetsMandatariDettagliList
    );

    // Handler per espandere/collassare
    const handleExpandClick = async () => {
        // if (!expanded && !dataLoaded) {
        if (!expanded) {
            // Prima volta che espando, carico i dati
            setLoading(true);
            try {
                await dispatch(fetchBudgetsCommercialiMandatari({
                    stagione_id: stagione_id,
                    mandatario_id: budgetMandatario.mandatario,
                    // commerciale_id:  budgetMandatario.commerciale
                }));
                setDataLoaded(true);
            } finally {
                setLoading(false);
            }
        }
        setExpanded(!expanded);
    };

    // Filtro i dettagli per questo mandatario specifico
    const dettagliItems = budgetsMandatariDettagli.budgetsMandatariDettagli.filter(
        det => det.mandatario === budgetMandatario.mandatario
    );

    // Funzione per colore stato
    const getStatoChipColor = (stato: string): "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" => {
        switch (stato) {
            case 'CONF': return 'success';
            case 'CAMP': return 'info';
            case 'PROT': return 'warning';
            case 'ANN': return 'error';
            default: return 'default';
        }
    };

    return (
        <Card sx={{backgroundColor: theme.palette.grey[50], mb: 1, p: 1}}>
            {/* Header con i totali del mandatario */}
            <Grid container spacing={1} alignItems="center">
                <Grid size={{ xs: 3 }}>
                    <Stack
                        direction="row"
                        spacing={1}
                        sx={{ alignItems: "center" }}
                    >
                        <Typography variant="body1" fontWeight="medium">
                            {budgetMandatario.mandatario_display}
                        </Typography>
                        <IconButton
                            onClick={handleExpandClick}
                            size="small"
                            sx={{ ml: 1 }}
                        >
                            {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                        </IconButton>
                    </Stack>
                </Grid>

                <Grid size={{ xs: 3 }} sx={{ px: 8 }}>
                    <ItemSmall elevation={1}>
                        <Typography variant="body2" fontWeight="bold">
                            {Intl.NumberFormat("it-IT", {
                                style: "decimal",
                                useGrouping: true,
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                            }).format(budgetMandatario.valore_budget)} €
                        </Typography>
                    </ItemSmall>
                </Grid>

                <Grid size={{ xs: 3 }} sx={{ px: 8 }}>
                    <ItemSmall elevation={1}>
                        <Typography variant="body2" fontWeight="bold">
                            {Intl.NumberFormat("it-IT", {
                                style: "decimal",
                                useGrouping: true,
                                minimumFractionDigits: 0,
                                maximumFractionDigits: 0,
                            }).format(budgetMandatario.valore_confermato)} €
                        </Typography>
                    </ItemSmall>
                </Grid>

                <Grid size={{ xs: 3 }} sx={{ px: 8 }}>
                    <ItemSmall elevation={1}>
                        <Typography variant="body2" fontWeight="bold">
                            {Intl.NumberFormat("it-IT", {
                                style: "decimal",
                                useGrouping: true,
                                minimumFractionDigits: 0,
                                maximumFractionDigits: 0,
                            }).format(budgetMandatario.valore_prodotto)} €
                        </Typography>
                        {budgetMandatario.percentuale_prodotto > 0 && (
                            <Typography variant="caption" color="text.secondary">
                                ({budgetMandatario.percentuale_prodotto.toFixed(0)}%)
                            </Typography>
                        )}
                    </ItemSmall>
                </Grid>
            </Grid>

            {/* Collapse per i dettagli degli item */}
            <Collapse in={expanded} timeout="auto" unmountOnExit>
                <Box sx={{ mt: 2, pl: 2 }}>
                    {loading ? (
                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            py: 2
                        }}>
                            <CircularProgress size={20} />
                        </Box>
                    ) : (
                        <>
                            {dettagliItems.length > 0 ? (
                                <Box>
                                    <Typography
                                        variant="caption"
                                        sx={{ mb: 1, fontWeight: 'bold' }}
                                    >
                                        Dettaglio Item
                                    </Typography>

                                    {/* Header della tabella */}
                                    <Card sx={{
                                        backgroundColor: theme.palette.grey[200],
                                        mb: 0.5,
                                        p: 0.5
                                    }}>
                                        <Grid container spacing={1}>
                                            <Grid size={{ xs: 1.2 }}>
                                                <Typography variant="caption" fontWeight="bold">
                                                    Codice
                                                </Typography>
                                            </Grid>
                                            <Grid size={{ xs: 3.3 }}>
                                                <Typography variant="caption" fontWeight="bold">
                                                    Descrizione
                                                </Typography>
                                            </Grid>
                                            <Grid size={{ xs: 1 }}>
                                                <Typography variant="caption" fontWeight="bold">
                                                    Fase Collezione
                                                </Typography>
                                            </Grid>
                                            <Grid size={{ xs: 1.4 }}>
                                                <Typography variant="caption" fontWeight="bold">
                                                    Tecnica
                                                </Typography>
                                            </Grid>
                                            <Grid size={{ xs: 1 }} sx={{ textAlign: 'right' }}>
                                                <Typography variant="caption" fontWeight="bold">
                                                    Q.tà
                                                </Typography>
                                            </Grid>
                                            <Grid size={{ xs: 1 }} sx={{ textAlign: 'right' }}>
                                                <Typography variant="caption" fontWeight="bold">
                                                    Px Prod
                                                </Typography>
                                            </Grid>
                                            <Grid size={{ xs: 1 }} sx={{ textAlign: 'right' }}>
                                                <Typography variant="caption" fontWeight="bold">
                                                    Budget
                                                </Typography>
                                            </Grid>
                                            <Grid size={{ xs: 1 }} sx={{ textAlign: 'right' }}>
                                                <Typography variant="caption" fontWeight="bold">
                                                    Conf.
                                                </Typography>
                                            </Grid>
                                            <Grid size={{ xs: 1 }} sx={{ textAlign: 'right' }}>
                                                <Typography variant="caption" fontWeight="bold">
                                                    Prod.
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                    </Card>

                                    {/* Righe degli item */}
                                    {dettagliItems.filter(d=>d.commerciale==budgetMandatario.commerciale) .map((item, index) => (
                                        <Card
                                            key={`${item.stagione}-${item.codice_item}-${index}`}
                                            sx={{
                                                backgroundColor: theme.palette.background.paper,
                                                mb: 0.5,
                                                p: 0.5,
                                                '&:hover': {
                                                    backgroundColor: theme.palette.action.hover
                                                }
                                            }}
                                        >
                                            <Grid container spacing={1} alignItems="center">
                                                <Grid size={{ xs: 1.2 }}>
                                                    <Typography variant="caption" fontWeight="medium" component={Link} to={Routes_path.PREVISIONE+item.id} target="_blank">
                                                        {item.codice_item}
                                                    </Typography>
                                                </Grid>

                                                <Grid size={{ xs: 3.3 }}>
                                                    <Typography variant="caption">
                                                        {item.descrizione_item}
                                                    </Typography>
                                                </Grid>

                                                <Grid size={{ xs: 1 }}>
                                                    <Chip
                                                        label={item.fase_collezione}
                                                        size="small"
                                                        color={getStatoChipColor(item.fase_collezione)}
                                                        sx={{ height: 20, fontSize: '0.7rem' }}
                                                    />
                                                </Grid>

                                                <Grid size={{ xs: 1.4 }}>
                                                    <Typography variant="caption">
                                                        {item.tecnica_display}
                                                    </Typography>
                                                </Grid>

                                                <Grid size={{ xs: 1 }} sx={{ textAlign: 'right' }}>
                                                    <Typography variant="caption">
                                                        {Intl.NumberFormat("it-IT", {
                                                            style: "decimal",
                                                            useGrouping: true,
                                                            minimumFractionDigits: 0,
                                                            maximumFractionDigits: 1,
                                                        }).format(item.qta_bdg||0)}
                                                    </Typography>
                                                </Grid>

                                                <Grid size={{ xs: 1 }} sx={{ textAlign: 'right' }}>
                                                    <Typography variant="caption">
                                                        {Intl.NumberFormat("it-IT", {
                                                            style: "decimal",
                                                            useGrouping: true,
                                                            minimumFractionDigits: 2,
                                                            maximumFractionDigits: 2,
                                                        }).format(item.px_prod||0) + " €"}
                                                    </Typography>
                                                </Grid>

                                                <Grid size={{ xs: 1 }} sx={{ textAlign: 'right' }}>
                                                    <Typography variant="caption" sx={{
                                                        // textAlign: 'right',
                                                        fontWeight: 'bold'
                                                    }}>
                                                        {Intl.NumberFormat("it-IT", {
                                                            style: "decimal",
                                                            useGrouping: true,
                                                            minimumFractionDigits: 2,
                                                            maximumFractionDigits: 2,
                                                        }).format(item.valore_budget||0) + " €"}
                                                    </Typography>
                                                </Grid>

                                                <Grid size={{ xs: 1 }} sx={{ textAlign: 'right' }}>
                                                    <Typography variant="caption" sx={{
                                                        // textAlign: 'right',
                                                        color: item.valore_confermato > 0 ? 'success.main' : 'text.secondary'
                                                    }}>
                                                        {Intl.NumberFormat("it-IT", {
                                                            style: "decimal",
                                                            useGrouping: true,
                                                            minimumFractionDigits: 2,
                                                            maximumFractionDigits: 2,
                                                        }).format(item.valore_confermato||0) + " €"}
                                                    </Typography>
                                                </Grid>

                                                <Grid size={{ xs: 1 }} sx={{ textAlign: 'right' }}>
                                                    <Typography variant="caption" sx={{
                                                        // textAlign: 'right',
                                                        color: item.valore_prodotto > 0 ? 'primary.main' : 'text.secondary'
                                                    }}>
                                                        {Intl.NumberFormat("it-IT", {
                                                            style: "decimal",
                                                            useGrouping: true,
                                                            minimumFractionDigits: 2,
                                                            maximumFractionDigits: 2,
                                                        }).format(item.valore_prodotto||0) + " €"}
                                                    </Typography>
                                                </Grid>
                                            </Grid>
                                        </Card>
                                    ))}
                                </Box>
                            ) : (
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ py: 1 }}
                                >
                                    Nessun dettaglio item disponibile
                                </Typography>
                            )}
                        </>
                    )}
                </Box>
            </Collapse>
        </Card>
    );
};

export default BudgetCommercialeMandatarioCard;